import isEmpty from 'lodash/isEmpty';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import NotImplemented from '../NotImplemented';
import SummaryBarUI from './SummaryBarUI';
import ctaClickGtmEvent from './utils/ctaClickGtmEvent';
import safelyStringifyJSON from '../../utils/safelyStringifyJSON';
import updateFieldsWithInventoryLeadParams from './utils/mapFields';
import useFetchCurrentVehiclePaymentByDealerProvince from '../TrimPayment/InventoryTrimPayment/useFetchCurrentVehiclePaymentByDealerProvince';
import useIncentiveOffers from '../PaymentCalculator/ViewOffers/useIncentiveOffers';
import useModelAndTrimInventoryStatus from './hooks/useModelAndTrimInventoryStatus';
import usePaymentTooltipParams from '../UniCard/hooks/usePaymentTooltipParams';
import { INVENTORY_CALCULATOR } from '../TrimPayment/constants';
import { INVENTORY_VARIANT } from './utils/constants';
import { createObjectFromProps } from '../../utils/object';
import { primaryCalculatorId, selectFinancialCalculator, selectPrimaryCalculatorPaymentOptions, } from '../../core/reducers/financialCalculators';
import { selectBodyTypesForGtm, selectTransmissionFromCurrentVehicle, selectVehicleByTrim, } from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import { selectDealer } from '../../core/reducers/dealer';
import { selectModelName, selectTrimName } from '../../core/reducers/ui';
import { useAppSelector } from '../../core/utils';
import { useCurrentVehicleDealerInventoryStatus } from '../../core/apiHooks/useFetchInventoryStatus';
import { useFetchCurrentVehiclePayment } from '../../core/apiHooks/useFetchPayment';
import { useGetCurrentBapPage } from '../../utils/sitecoreContext';
import useSellingPriceConfiguration from '../PriceComponent/useSellingPriceConfiguration';
const constructPrices = (paymentResponse, defaultPaymentResponse, t, isInventoryVariant) => {
    var _a, _b, _c, _d;
    if (isInventoryVariant) {
        return {
            allInPrice: {
                ...(_a = paymentResponse === null || paymentResponse === void 0 ? void 0 : paymentResponse.data) === null || _a === void 0 ? void 0 : _a.sellingPrice,
                label: t('Shared.Common.sellingPriceLabel'),
            },
            msrpPrice: {
                ...(_b = paymentResponse === null || paymentResponse === void 0 ? void 0 : paymentResponse.data) === null || _b === void 0 ? void 0 : _b.msrp,
                label: t('Shared.Common.msrpStartingFromLabel'),
            },
        };
    }
    return {
        allInPrice: {
            ...(_c = defaultPaymentResponse === null || defaultPaymentResponse === void 0 ? void 0 : defaultPaymentResponse.data) === null || _c === void 0 ? void 0 : _c.sellingPrice,
            label: t('Shared.Common.sellingPriceLabel'),
        },
        msrpPrice: {
            ...(_d = defaultPaymentResponse === null || defaultPaymentResponse === void 0 ? void 0 : defaultPaymentResponse.data) === null || _d === void 0 ? void 0 : _d.sellingPrice,
            label: t('Shared.Common.sellingPriceLabel'),
        },
    };
};
const SummaryBarMap = ({ fields, rendering, params }) => {
    const mappedFields = useMemo(() => createObjectFromProps(fields), [safelyStringifyJSON(fields)]);
    const { trimKey, modelKey, modelYear, transmissionKey, exteriorColorKey, interiorColorKey } = useAppSelector(selectCurrentVehicle);
    const { dealerCode = '' } = useSelector(selectDealer);
    const { isSummaryPage } = useGetCurrentBapPage();
    const isInventoryVariant = (params === null || params === void 0 ? void 0 : params.variant) === INVENTORY_VARIANT;
    const mappedVariantFields = isInventoryVariant
        ? updateFieldsWithInventoryLeadParams(mappedFields, [
            { key: 'model', value: modelKey },
            { key: 'modelYear', value: modelYear },
            { key: 'trim', value: trimKey },
            { key: 'transmission', value: transmissionKey },
            { key: 'exteriorColor', value: exteriorColorKey },
            { key: 'interiorColor', value: interiorColorKey },
            { key: 'dealerCode', value: dealerCode },
        ])
        : mappedFields;
    const { t } = useTranslation();
    const transmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const trimName = useAppSelector(selectTrimName);
    const modelName = useAppSelector(selectModelName);
    useIncentiveOffers(primaryCalculatorId, undefined, isInventoryVariant);
    const vehicleName = trimKey && trimName && transmission
        ? `${modelYear} ${modelName} ${trimName} ${transmission === null || transmission === void 0 ? void 0 : transmission.transmissionName}`
        : '';
    const gtmBodyStyle = useSelector(selectBodyTypesForGtm);
    const trim = useAppSelector(state => selectVehicleByTrim(state, trimKey));
    const primaryCalculator = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const inventoryCalculator = useAppSelector(state => selectFinancialCalculator(state, INVENTORY_CALCULATOR));
    const { paymentMethod, paymentFrequency, overriddenIsSellingProvince, includeFees } = isInventoryVariant
        ? inventoryCalculator !== null && inventoryCalculator !== void 0 ? inventoryCalculator : primaryCalculator
        : primaryCalculator;
    const inventoryPaymentResponse = useFetchCurrentVehiclePaymentByDealerProvince(INVENTORY_CALCULATOR, !!inventoryCalculator && isInventoryVariant);
    const defaultPaymentResponse = useFetchCurrentVehiclePayment(undefined, !isInventoryVariant);
    const paymentResponse = inventoryPaymentResponse;
    const paymentTooltipParams = usePaymentTooltipParams({
        product: {
            year: trim === null || trim === void 0 ? void 0 : trim.modelYear,
            model: trim === null || trim === void 0 ? void 0 : trim.modelName,
            trim: trim === null || trim === void 0 ? void 0 : trim.trimName,
        },
        payment: isInventoryVariant ? paymentResponse.data : defaultPaymentResponse.data,
        overriddenIsSellingProvince: !!overriddenIsSellingProvince,
    });
    const prices = constructPrices(paymentResponse, defaultPaymentResponse, t, isInventoryVariant);
    const priceErrorMessage = t('Pages.Bap.apiLoadingError');
    const { sellingPriceTooltipLabelValue } = useSellingPriceConfiguration();
    const sellingPriceTooltipLabel = includeFees
        ? t('Shared.Common.sellingPriceDisclaimerWithTaxLabel')
        : sellingPriceTooltipLabelValue;
    const msrpTooltipLabel = t('Shared.Common.msrpDisclaimerLabel');
    const { data: inventoryStatus } = useCurrentVehicleDealerInventoryStatus(); // for inventory badge and GTM event
    const modelAndTrimInventoryStatus = useModelAndTrimInventoryStatus(!isInventoryVariant) || ''; // for BAP GTM event
    const onCtaClickHandler = (linkText, ctaType, linkUrl) => {
        ctaClickGtmEvent({
            event: isInventoryVariant ? 'dealer_inventory_cta_click' : 'bap_cta',
            linkText,
            ctaType,
            linkUrl,
            ctaPlacement: isInventoryVariant ? `dealer inventory : ${inventoryStatus}` : 'customize sticky',
            componentType: rendering.componentName,
            modelName: trim === null || trim === void 0 ? void 0 : trim.modelName,
            trimName: trim === null || trim === void 0 ? void 0 : trim.trimName,
            bodyStyle: gtmBodyStyle,
            availabilityFlag: isInventoryVariant ? inventoryStatus : modelAndTrimInventoryStatus,
        });
    };
    if (!fields || isEmpty(fields) || !mappedFields) {
        return React.createElement(NotImplemented, { name: "SummaryBar" });
    }
    return (React.createElement(SummaryBarUI, { variant: params.variant, componentName: rendering === null || rendering === void 0 ? void 0 : rendering.componentName, fields: mappedVariantFields, paymentMethod: paymentMethod, paymentFrequency: paymentFrequency, paymentTooltipParams: paymentTooltipParams, prices: prices, priceErrorMessage: priceErrorMessage, sellingPriceTooltipLabel: sellingPriceTooltipLabel, msrpTooltipLabel: msrpTooltipLabel, paymentResponse: isInventoryVariant ? paymentResponse : defaultPaymentResponse, overriddenIsSellingProvince: overriddenIsSellingProvince, inventoryStatus: inventoryStatus, vehicleName: vehicleName, isSummaryPage: isSummaryPage, onCtaClickHandler: onCtaClickHandler }));
};
export default SummaryBarMap;
